import { AppointmentPhase, Status } from "components";
import PublicationToWebIcon from "components/PublicationToWeb/PublicationToWebIcon";
import { type CardSections } from "components/Tables/CardList";
import TagList from "components/Tag/TagList";
import VisibleToExternalIcon from "components/VisibleToExternal/VisibleToExternalIcon";
import moment from "moment";
import React from "react";
import CostSalesForList from "show/components/CostSalesForList";
import { formatDayWithDate, formatTime, formatTimeDifference } from "utilities";
import InvoiceIndication from "./list/InvoiceIndication";
import DocumentIndication from "./list/DocumentIndication";
import { useViewport } from "utilities/hookutils";
import { getRealStartEndDate } from "utilities/showDateUtils";
import { PhosphorWarningInline } from "components/Atoms/PhosphorIcon";
import { LargeClickableTitle } from "components/ListTemplate/LargeClickableTitle";
import { ShowSidePanel } from "components/SidePanel/Show/ShowSidePanel";

const ShowTitleElement = ({ show }: { show: server.dto.ShowForListInternal }) => {
  const hasProduction = (show.productionName);
  const eventName = show.eventName || "...";
  const artistName = site.currentEnvironment.functionGroups.artist.enabled && (show.artist) ? `${show.artist.name} | ` : null;
  return <>
    <ShowSidePanel
      showId={show.showId}
      trigger={<LargeClickableTitle
        icon={<>
          <AppointmentPhase value={show.status.appointmentPhase} displayLabel={false} />
          {show.locked === "locked" && <i className="icon-lock"></i>}
        </>}
        title={<>
          {!show.validated && <PhosphorWarningInline />}
          {artistName}{eventName}
        </>} />}
      canEdit={show.permissions.canModify}
    />
    {hasProduction && <div>{show.productionName}</div>}
  </>
};

const ShowDate = ({ show }: { show: server.dto.ShowForListInternal | server.dto.ShowForListExternal }) => {
  const startDisplayMoment = moment.utc(show.dateTime.startDisplay);
  const endDisplayMoment = moment.utc(show.dateTime.endDisplay);
  const { startRealDate, endRealDate } = getRealStartEndDate({
    startDisplayDate: startDisplayMoment,
    endDisplayDate: endDisplayMoment,
    timeOfPerformance: show.dateTime.customFallbackTime,
    mode: show.dateTime.mode
  });
  const timeDifference = formatTimeDifference(startRealDate, endRealDate);

  const longerThanOneDay = !startDisplayMoment.isSame(endDisplayMoment, "day");

  if (longerThanOneDay) {
    return <div className="show-list-card-date" title={timeDifference}>
      {formatDayWithDate(startDisplayMoment)} » {formatDayWithDate(endDisplayMoment)}
    </div>;
  }

  return <div className="show-list-card-date" title={timeDifference}>
    {formatDayWithDate(startDisplayMoment)}
  </div>;
};

const ShowTime = ({ show }: { show: server.dto.ShowForListInternal | server.dto.ShowForListExternal }) => {
  if (show.dateTime.mode === "allDay") {
    if (!show.dateTime.customFallbackTime) return null;

    return <span className="s1_blue s1_small">{show.dateTime.customFallbackTime}</span>;
  }

  const startDisplayMoment = moment.utc(show.dateTime.startDisplay);
  const endDisplayMoment = moment.utc(show.dateTime.endDisplay);

  const isSameMoment = startDisplayMoment.isSame(endDisplayMoment);
  if (isSameMoment) {
    return <span>{formatTime(startDisplayMoment)}</span>;
  }

  const isSameDay = startDisplayMoment.isSame(endDisplayMoment, "day");
  if (isSameDay) {
    return <span>{formatTime(startDisplayMoment)} - {formatTime(endDisplayMoment)}</span>;
  }

  return <span>{formatTime(startDisplayMoment)} - <span className="s1_gray">{formatTime(endDisplayMoment)}</span></span>;
};

export const DateTimeAndStatus = ({ show }: { show: server.dto.ShowForListInternal | server.dto.ShowForListExternal }) =>
  <div className="flex-column gap-small">
    <div>
      <ShowDate show={show} />
      <ShowTime show={show} />
    </div>
    <div className="">
      <Status status={show.status} />
    </div>
  </div>;

export const VenuePromoterHotel = ({ show }: { show: server.dto.ShowForListInternal | server.dto.ShowForListExternal }) => {
  const hasVenue = site.currentEnvironment.functionGroups.venue.enabled && (show.venue);
  const hasPromoter = site.currentEnvironment.functionGroups.promoter.enabled && (show.promoterName);
  const hasHotel = site.currentEnvironment.functionGroups.hotel.enabled && (show.hotelName);

  const venueName = `${show.venue?.name} ${show.venue?.cityCountry ? `, ${show.venue.cityCountry}` : ""}`;

  return <div className="show-list-card">
    {hasVenue && <>
      <div className="show-list-card-label">{site.currentEnvironment.functionGroups.venue.singular}</div>
      <div className="text-ellipsis" title={venueName}>
        {venueName}
      </div>
    </>}

    {hasPromoter && <>
      <span className="show-list-card-label ">{site.currentEnvironment.functionGroups.promoter.singular}</span>
      <span className="text-ellipsis" title={show.promoterName}>{show.promoterName}</span>
    </>}

    {hasHotel && <>
      <span className="show-list-card-label">{site.currentEnvironment.functionGroups.hotel.singular}</span>
      <span className="text-ellipsis" title={show.hotelName}>{show.hotelName}</span>
    </>}
  </div>;
};

const Financial = ({ show }: { show: server.dto.ShowForListInternal }) => {
  const viewport = useViewport();
  const hasCosts = site.permissions.show.canReadCosts && show.costsExcludingTax.amount !== 0;
  const hasSales = site.permissions.show.canReadSales && show.salesExcludingTax.amount !== 0;

  const hasProjectCode = Boolean(show.projectCode);
  const hasCostCenter = Boolean(show.costCenter);

  const hasAnyFinancial = hasCosts || hasSales || hasProjectCode || hasCostCenter;
  if (!hasAnyFinancial && viewport === "mobile") return null;

  return <div className="show-list-card">
    {hasCosts && <>
      <div className="show-list-card-label">{RESX.Financial.Costs}</div>
      <div className="text-ellipsis" title={show.costsExcludingTax.formatted}><CostSalesForList value={show.costsExcludingTax} baseCurrencyFormat="hidden" /></div>
    </>}

    {hasSales && <>
      <div className="show-list-card-label">{RESX.Financial.Sales}</div>
      <div className="text-ellipsis" title={show.salesExcludingTax.formatted}><CostSalesForList value={show.salesExcludingTax} baseCurrencyFormat="hidden" /></div>
    </>}

    {hasProjectCode && <>
      <div className="show-list-card-label">{RESX.Show.ProjectCode}</div>
      <div className="text-ellipsis" title={show.projectCode}>{show.projectCode}</div>
    </>}

    {hasCostCenter && <>
      <div className="show-list-card-label">{RESX.CostCenter.resxCostCenter}</div>
      <div className="text-ellipsis" title={show.costCenter.title}>{show.costCenter.internalId}</div>
    </>}
  </div>;
};

const DocumentIndications = ({ show }: { show: server.dto.ShowForListInternal }) => {
  return <>
    {show.documentIndications.map((indication, idx) =>
      indication.documentType === "invoice"
        ? <InvoiceIndication
          key={`${idx}_${indication.color}_${indication.count}_${indication.documentCode}`}
          documentIndication={indication}
          showId={show.showId}
        />
        : <DocumentIndication
          key={`${idx}_${indication.color}_${indication.count}_${indication.documentCode}`}
          documentIndication={indication}
          showId={show.showId}
        />)}
  </>;
};

const FooterRight = ({ show }: { show: server.dto.ShowForListInternal }) =>
  <div className="flex gap-small align-end">
    <DocumentIndications show={show} />
    <VisibleToExternalIcon visibleToExternal={show.visibleToExternal} style={{ width: 24 }} />
    <PublicationToWebIcon publicationToWeb={show.publicationToWeb} style={{ width: 24 }} />
  </div>;

export const cardLayout = (_: server.viewmodel.ShowListInternalFrontModel): CardSections<server.dto.ShowForListInternal> => ({
  renderTitle: show => <ShowTitleElement show={show} />,
  hideTitle: () => false,
  renderBody: show => <>
    <DateTimeAndStatus show={show} />
    <VenuePromoterHotel show={show} />
    <Financial show={show} />
  </>,
  renderFooter: show => <>
    <div style={{ flex: 1 }}>
      <TagList tags={show.tags} length="short" />
    </div>
    <FooterRight show={show} />
  </>,
  hideFooter: () => false
});